import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './LoginScreen.module.css';
import { UserService } from '../../api/services/UserService';
import { useTranslation } from '../../i18n/provider';

// Mirrors the backend's VALIDATION.MIN_PASSWORD_LENGTH (gym-planner-backend/src/config/constants.ts).
// Client-side check is purely UX (avoid a round trip for an obviously-too-short password) - the
// backend re-validates and is the actual source of truth.
const MIN_PASSWORD_LENGTH = 6;

/**
 * TODO.md J1 — password reset form, reached via the emailed `/reset-password?token=...` link.
 *
 * A missing token in the URL and a token the backend rejects (never existed, expired, or already
 * used - the backend deliberately does not distinguish) both render the same "invalid or expired"
 * state with a link back to /forgot-password.
 */
export const ResetPasswordScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [invalidLink, setInvalidLink] = useState(false);

  const userService = new UserService();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInvalidLink(false);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(t('resetPassword.tooShort', { count: MIN_PASSWORD_LENGTH }));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.mismatch'));
      return;
    }

    setIsLoading(true);
    try {
      await userService.resetPassword(token, newPassword);
      navigate('/login', { state: { passwordResetSuccess: true } });
    } catch (err: any) {
      setInvalidLink(true);
      setError(err.message || t('resetPassword.invalidLink'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.loginScreen}>
        <div className={styles.loginContainer}>
          <div className={styles.loginIcon}>⚠️</div>
          <h1 className={styles.loginTitle}>{t('resetPassword.title')}</h1>
          <p className={styles.errorMessage} data-testid="reset-password-invalid-link">
            {t('resetPassword.invalidLink')}
          </p>
          <Link
            to="/forgot-password"
            className={styles.toggleButton}
            data-testid="reset-password-request-new-link"
          >
            {t('resetPassword.requestNewLink')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.loginScreen}>
      <div className={styles.loginContainer}>
        <div className={styles.loginIcon}>🔒</div>
        <h1 className={styles.loginTitle}>{t('resetPassword.title')}</h1>
        <p className={styles.loginMessage}>{t('resetPassword.prompt')}</p>
        <form onSubmit={handleSubmit} className={styles.authForm}>
          {error && (
            <div className={styles.errorMessage} data-testid="reset-password-error">
              {error}
            </div>
          )}
          <input
            type="password"
            placeholder={t('resetPassword.newPasswordPlaceholder')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={styles.authInput}
            required
            disabled={isLoading}
            data-testid="reset-password-new-password-input"
          />
          <input
            type="password"
            placeholder={t('resetPassword.confirmPasswordPlaceholder')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={styles.authInput}
            required
            disabled={isLoading}
            data-testid="reset-password-confirm-password-input"
          />
          <button
            type="submit"
            className={`${styles.loginButton} ${styles.loginButtonPrimary}`}
            disabled={isLoading}
            data-testid="reset-password-submit-button"
          >
            {isLoading ? t('auth.pleaseWait') : t('resetPassword.submit')}
          </button>
        </form>
        {invalidLink && (
          <div className={styles.toggleAuth}>
            <Link
              to="/forgot-password"
              className={styles.toggleButton}
              data-testid="reset-password-request-new-link"
            >
              {t('resetPassword.requestNewLink')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
